import React from "react";
import axios from "axios";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { captureErrorMessage } from "@/lib/helpers/captureErrorMessage";

/**
 * Healthcare-specific React Query configuration
 *
 * @see README.md for detailed HIPAA & Healthcare compliance considerations
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: unknown) => {
        // Healthcare-specific retry logic
        if (axios.isAxiosError(error)) {
          // Don't retry auth errors
          if (error.response?.status === 401) return false;

          // Don't retry client errors (4xx)
          if (error.response?.status >= 400 && error.response?.status < 500) return false;

          // Don't retry fatal server errors
          if (error.response?.status === 500) return false;

          // Retry network/timeout errors and recoverable server errors (502, 503, 504)
          if (
            error.code === "NETWORK_ERROR" ||
            error.code === "ECONNABORTED" ||
            [502, 503, 504].includes(error.response?.status || 0)
          ) {
            return failureCount < 3;
          }
        }

        return failureCount < 3;
      },
      refetchOnWindowFocus: false, // Healthcare apps shouldn't auto-refetch on focus
      refetchOnReconnect: true, // But should refetch on network reconnect
    },
    mutations: {
      retry: 0, // don't auto retry
      onError: (error) => {
        // LogRocket integration for error tracking (HIPAA-compliant)
        const errorMessage = axios.isAxiosError(error)
          ? `API Mutation Error: ${error.response?.status} ${error.response?.statusText || error.message}`
          : `Mutation Error: ${error instanceof Error ? error.message : "Unknown error"}`;

        captureErrorMessage(errorMessage, {
          tags: {
            errorType: "mutation",
            statusCode: axios.isAxiosError(error) ? error.response?.status : undefined,
            url: axios.isAxiosError(error) ? error.config?.url : undefined,
          },
        });
      },
    },
  },
});

type QueryProviderProps = {
  children: React.ReactNode;
};

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
